<template>
  <section class="search-filter">
    <div
      id="search-share-link"
      style="width: 1px; height: 1px; overflow: hidden;"
    >
      {{ shareLink }}
    </div>

    <div class="search-filter__header">
      <search-checkbox
        :availableOnly="searchAvailableOnly"
        :title="labels.availableOnly"
        @availability-change="handleAvailableOnlyChange"
      />
      <svg
        width="26"
        height="26"
        class="search-filter__share"
        @click="handleShareLink"
      >
        <title>Поделиться ссылкой на поисковые результаты с фильтрами</title>
        <use xlink:href="#share" />
      </svg>
    </div>

    <search-sorting />

    <search-filter-select
      :title="labels.types"
      :options-loader="getTypes"
      :value="searchTypes"
      :multiple="true"
      :organization=false
      placeholder="Начните вводить название индекса"
      no-result-text="Возможно вы опечатались или такой индекс документа отсутствует."
      prompt-text="Фильтр по индексам документов. Например, искать по ГОСТам, распоряжениям, КПИ"
      @input="handleTypesChanged"
    />

    <search-list
      :listLabel="labels.classifiers"
      :listType="'classifiers'"
      :listData="selectedClassifiers"
      @toggleSidebar="$emit('toggle-sidebar', 'classifiers')"
      @unselectItem="unselectClassifier"
    />

    <search-list
      :listLabel="labels.checklists"
      :listType="'checklists'"
      :listData="selectedChecklists"
      @toggleSidebar="$emit('toggle-sidebar', 'checklists')"
      @unselectItem="unselectChecklist"
    />

    <search-statuses
      :statuses="documentStatuses"
      :searchStatuses="searchStatuses"
      :title="labels.statuses"
      @status-changed="handleStatusChanged"
    />

    <search-filter-select
      :title="labels.organizations"
      :options-loader="getOrganizations"
      :value="searchOrganizations"
      :multiple="true"
      :approved-from-value="searchApprovedFrom"
      :approved-to-value="searchApprovedTo"
      :organization=true
      placeholder="Начните вводить название организации"
      no-result-text="Возможно вы опечатались или такая организация отсутствует"
      prompt-text="Фильтр по организации, утвердившей документ"
      @input="handleOrganizationsChanged"
      @dates-approved-changed="handleDatesApprovedChanged"
    />

    <search-filter-select
      :title="labels.designers"
      :options-loader="getDesigners"
      :value="searchDesigners"
      :multiple="true"
      placeholder="Начните вводить название организации"
      no-result-text="Возможно вы опечатались или такая организация отсутствует"
      prompt-text="Фильтр по организации, разработавшей документ"
      @input="handleDesignersChanged"
    />

    <search-dates
      :title="labels.dates"
      :modified-from-value="searchDateModifiedFrom"
      :modified-to-value="searchDateModifiedTo"
      :entered-from-value="searchDateEnteredFrom"
      :entered-to-value="searchDateEnteredTo"
      :canceled-from-value="searchDateCanceledFrom"
      :canceled-to-value="searchDateCanceledTo"
      @dates-changed="handleDatesChanged"
    />

    <search-added-in-base
      :title="labels.addedInBase"
      :b-month="searchAddedIsMonth"
      :day-from-value= "searchAddedFromDay"
      :day-to-value="searchAddedToDay"
      :month-from-value="searchAddedMonthFrom"
      :month-to-value="searchAddedMonthTo"
      @dates-add-changed="handleDatesAddedInBaseChanged"
    />
  </section>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import apiDocuments from '@src/api/documents';

import SearchCheckbox from './SearchCheckbox';
import SearchFilterSelect from './SearchFilterSelect';
import SearchSorting from './SearchSorting';
import SearchList from './SearchList';
import SearchStatuses from './SearchStatuses';
import SearchDates from './SearchDates';
import SearchAddedInBase from './SearchAddedInBase';

const fieldLabels = {
  types: 'Индексы',
  classifiers: 'Классификаторы',
  checklists: 'Перечни',
  statuses: 'Статус документа',
  organizations: 'Утвержден',
  availableOnly: 'Только доступные документы',
  openInWindow: 'Открывать в новой вкладке',
  dates: 'По датам',
  addedInBase: 'По дате добавления или изменения документа в базе',
  designers: 'Разработан'
};

export default {
  components: {
    SearchCheckbox,
    SearchFilterSelect,
    SearchSorting,
    SearchList,
    SearchStatuses,
    SearchDates,
    SearchAddedInBase
  },

  data() {
    return {
      labels: fieldLabels
    };
  },

  computed: {
    ...mapState(['documentStatuses']),
    ...mapGetters([
      'searchAvailableOnly',
      'selectedClassifiers',
      'selectedChecklists',
      'searchTypes',
      'searchOrganizations',
      'searchDesigners',
      'searchStatuses',
      'queryAsString',
      'searchApprovedFrom',
      'searchApprovedTo',
      'searchDateModifiedFrom',
      'searchDateModifiedTo',
      'searchDateEnteredFrom',
      'searchDateEnteredTo',
      'searchDateCanceledFrom',
      'searchDateCanceledTo',
      'searchAddedIsMonth',
      'searchAddedFromDay',
      'searchAddedToDay',
      'searchAddedMonthFrom',
      'searchAddedMonthTo'
    ]),
    shareLink() {
      return this.queryAsString
        ? `${window.location.origin}/search/share?${this.queryAsString}`
        : '';
    }
  },

  methods: {
    handleShareLink() {
      if (!this.queryAsString) {
        this.$store.dispatch('notificationCreateToast', {
          type: 'error',
          text: `Ссылка отсутствует`
        });
      } else {
        if (navigator.share) {
          this.navigatorShareLink();
        } else if (navigator.clipboard) {
          this.copyLinkWithClipboard();
        } else {
          this.copyLinkWithExecCommand();
        }
      }
    },
    navigatorShareLink() {
      navigator
        .share({
          title: this.$store.getters.searchQueryText,
          url: this.shareLink
        })
        .then()
        .catch(this.shareLinkError);
    },
    copyLinkWithClipboard() {
      navigator.clipboard
        .writeText(this.shareLink)
        .then(this.shareLinkSuccess())
        .catch(this.shareLinkError);
    },
    copyLinkWithExecCommand() {
      let range = document.createRange();

      range.selectNodeContents(document.getElementById('search-share-link'));
      window.getSelection().removeAllRanges();
      window.getSelection().addRange(range);
      try {
        document.execCommand('copy');
        this.shareLinkSuccess();
      } catch (err) {
        this.shareLinkError(err);
      }
      window.getSelection().removeRange(range);
    },
    shareLinkSuccess() {
      this.$store.dispatch('notificationCreateToast', {
        type: 'success',
        text: 'Ссылка скопирована в буфер'
      });
    },
    shareLinkError(err) {
      this.$store.dispatch('notificationCreateToast', {
        type: 'error',
        text: `Во время копировании ссылки в буфер произошла ошибка: ${
          err.message
        }`
      });
    },
    getTypes(str) {
      return str ? apiDocuments.getTypes(str) : Promise.resolve([]);
    },
    getOrganizations(str) {
      return str ? apiDocuments.getOrganizations(str) : Promise.resolve([]);
    },
    getDesigners(str) {
      return str ? apiDocuments.getDesigners(str) : Promise.resolve([]);
    },
    handleAvailableOnlyChange(value) {
      this.$store.dispatch('changeQueryParamAndUpdateResults', {
        param: 'availableOnly',
        value: value
      });
    },
    handleTypesChanged(arr) {
      this.$store.dispatch('changeQueryParamAndUpdateResults', {
        param: 'types',
        value: arr
      });
    },
    handleOrganizationsChanged(obj) {
      this.$store.dispatch('changeQueryParamAndUpdateResults', {
        param: 'organizations',
        value: obj
      });
    },
    handleDesignersChanged(obj) {
      this.$store.dispatch('changeQueryParamAndUpdateResults', {
        param: 'designers',
        value: obj
      });
    },
    handleStatusChanged(obj) {
      this.$store.dispatch('changeQueryParamAndUpdateResults', {
        param: 'statuses',
        value: {
          checked: !obj.isChecked,
          id: obj.status.id,
          name: obj.status.name
        }
      });
    },
    handleDatesChanged(obj) {
      this.$store.dispatch('changeQueryParamAndUpdateResults', {
        param: 'dates',
        value: obj
      });
    },
    handleDatesApprovedChanged(obj) {
      this.$store.dispatch('changeQueryParamAndUpdateResults', {
        param: 'datesApprove',
        value: obj
      });
    },
    handleDatesAddedInBaseChanged(obj) {
      this.$store.dispatch('changeQueryParamAndUpdateResults', {
        param: 'datesAddedInBase',
        value: obj
      });
    },
    unselectClassifier(ind) {
      this.$store.dispatch('changeClassifiersQuery', {
        selected: true,
        classifier: this.selectedClassifiers[ind]
      });
    },
    unselectChecklist(ind) {
      this.$store.dispatch('changeChecklistsQuery', {
        mode: 'remove',
        value: this.selectedChecklists[ind]
      });
    }
  }
};
</script>
